// Granite Peak Manufacturing - physics-based machining math engine
// Ported verbatim from rigidity-labs/src/components/calculator/formulas.jsx
(function (global) {
'use strict';
// Physics-Based Machining Math Engine
// Calculates feeds & speeds from material properties and machine constraints.

// ─── Material Library ─────────────────────────────────────────────────────────
// tensileUTS in PSI, hardnessHB in Brinell
// sfmBase: recommended SFM for carbide, chipLoadBase: recommended chip load per flute (in) for 0.5" tool
const MATERIAL_LIBRARY = [
  { name: 'Aluminum 6061',      category: 'Aluminum',      hardnessHB: 95,  tensileUTS: 40000,  sfmBase: 1200, chipLoadBase: 0.004  },
  { name: 'Aluminum 7075',      category: 'Aluminum',      hardnessHB: 150, tensileUTS: 83000,  sfmBase: 1000, chipLoadBase: 0.0035 },
  { name: 'Mild Steel 1018',    category: 'Steel',         hardnessHB: 126, tensileUTS: 64000,  sfmBase: 350,  chipLoadBase: 0.002  },
  { name: 'Alloy Steel 4140',   category: 'Steel',         hardnessHB: 197, tensileUTS: 95000,  sfmBase: 250,  chipLoadBase: 0.0015 },
  { name: 'Stainless 304',      category: 'Stainless',     hardnessHB: 200, tensileUTS: 73000,  sfmBase: 150,  chipLoadBase: 0.0012 },
  { name: 'Stainless 316',      category: 'Stainless',     hardnessHB: 217, tensileUTS: 79000,  sfmBase: 130,  chipLoadBase: 0.001  },
  { name: 'Tool Steel D2',      category: 'Tool Steel',    hardnessHB: 610, tensileUTS: 200000, sfmBase: 80,   chipLoadBase: 0.0006 },
  { name: 'Tool Steel H13',     category: 'Tool Steel',    hardnessHB: 540, tensileUTS: 185000, sfmBase: 100,  chipLoadBase: 0.0007 },
  { name: 'Cast Iron',          category: 'Cast Iron',     hardnessHB: 210, tensileUTS: 30000,  sfmBase: 300,  chipLoadBase: 0.002  },
  { name: 'Titanium Ti-6Al-4V', category: 'Titanium',      hardnessHB: 334, tensileUTS: 130000, sfmBase: 100,  chipLoadBase: 0.0008 },
  { name: 'Brass 360',          category: 'Non-Ferrous',   hardnessHB: 80,  tensileUTS: 45000,  sfmBase: 600,  chipLoadBase: 0.003  },
  { name: 'Copper',             category: 'Non-Ferrous',   hardnessHB: 60,  tensileUTS: 32000,  sfmBase: 400,  chipLoadBase: 0.003  },
  { name: 'Inconel 718',        category: 'Superalloy',    hardnessHB: 400, tensileUTS: 185000, sfmBase: 60,   chipLoadBase: 0.0005 },
  { name: 'Plastic / Delrin',   category: 'Plastic',       hardnessHB: 20,  tensileUTS: 10000,  sfmBase: 600,  chipLoadBase: 0.005  },
];

// Coating multiplier on surface speed
const COATING_MULTIPLIERS = {
  'Uncoated': 1.0,
  'TiN': 1.1,
  'TiAlN': 1.25,
  'AlTiN': 1.3,
  'DLC': 1.2,
  'ZrN': 1.15,
};

const STRATEGY_MULTIPLIERS = {
  Balanced: { sfm: 1.0, chip: 1.0 },
  'Max MRR': { sfm: 1.08, chip: 1.15 },
  'Tool Life': { sfm: 0.82, chip: 0.9 },
  'Surface Finish': { sfm: 1.05, chip: 0.75 },
};

const INCH_TO_MM = 25.4;
const E_CARBIDE = 90e6;
const E_HSS = 30e6;
const HP_CONSTANT = 63025; // torque-lb-in * rpm / constant

const OPERATION_MULTIPLIERS = {
  'General Milling': { sfm: 1.0, chip: 1.0 },
  'Adaptive Roughing': { sfm: 1.1, chip: 1.15 },
  Slotting: { sfm: 0.85, chip: 0.75 },
  Finishing: { sfm: 1.15, chip: 0.55 },
};

const DRILLING_OPERATION_MULTIPLIERS = {
  'Standard Drill': { sfm: 1.0, fpr: 1.0, peck: 1.0 },
  'Deep Hole': { sfm: 0.82, fpr: 0.8, peck: 0.65 },
  'Pilot Hole': { sfm: 1.05, fpr: 0.9, peck: 1.15 },
  'Spot Drill': { sfm: 0.75, fpr: 0.55, peck: 1.4 },
};

const DRILLING_STRATEGY_MULTIPLIERS = {
  Balanced: { sfm: 1.0, fpr: 1.0, peck: 1.0 },
  Aggressive: { sfm: 1.08, fpr: 1.12, peck: 1.15 },
  Conservative: { sfm: 0.86, fpr: 0.82, peck: 0.75 },
};

const TAPPING_STRATEGY_MULTIPLIERS = {
  Balanced: { rpm: 1.0, torque: 1.0 },
  'Tool Life': { rpm: 0.75, torque: 0.9 },
  Aggressive: { rpm: 1.2, torque: 1.08 },
};

const TAP_ALLOWABLE_SHEAR_KSI = {
  HSS: 36,
  'Powdered Metal': 45,
  Carbide: 58,
};

const NUMBER_DRILL_SIZES_IN = [
  0.0135, 0.0145, 0.0156, 0.016, 0.017, 0.018, 0.019, 0.02, 0.021, 0.022, 0.023, 0.024,
  0.025, 0.026, 0.028, 0.0292, 0.031, 0.032, 0.033, 0.034, 0.035, 0.036, 0.037, 0.038,
  0.039, 0.04, 0.041, 0.042, 0.043, 0.0465, 0.052, 0.055, 0.0595, 0.0635, 0.067, 0.07,
  0.073, 0.076, 0.0785, 0.081, 0.082, 0.086, 0.089, 0.0935, 0.096, 0.098, 0.0995, 0.1015,
  0.104, 0.1065, 0.109, 0.11, 0.111, 0.113, 0.116, 0.12, 0.125, 0.1285, 0.136, 0.14,
  0.144, 0.147, 0.1495, 0.152, 0.154, 0.157, 0.159, 0.161, 0.166, 0.1695, 0.173, 0.177,
  0.18, 0.185, 0.189, 0.191, 0.1935, 0.196, 0.199, 0.201, 0.204, 0.2055, 0.209, 0.213,
  0.2188, 0.221, 0.228, 0.234, 0.238, 0.242, 0.246, 0.25,
];

const LETTER_DRILL_SIZES_IN = [
  0.234, 0.238, 0.242, 0.246, 0.25, 0.257, 0.261, 0.266, 0.272, 0.277, 0.281, 0.29, 0.295,
  0.302, 0.316, 0.323, 0.332, 0.339, 0.348, 0.358, 0.368, 0.377, 0.386, 0.397, 0.404, 0.413,
];

const FRACTIONAL_DRILL_SIZES_IN = Array.from({ length: 31 }, (_, i) => (i + 1) / 64);

const STANDARD_DRILL_SIZES_IN = Array.from(
  new Set([...NUMBER_DRILL_SIZES_IN, ...LETTER_DRILL_SIZES_IN, ...FRACTIONAL_DRILL_SIZES_IN]),
).sort((a, b) => a - b);

const METRIC_DRILL_SIZES_MM = Array.from({ length: 198 }, (_, i) => (i + 3) / 10);
const STANDARD_METRIC_DRILL_SIZES_IN = METRIC_DRILL_SIZES_MM.map(sizeMm => sizeMm / INCH_TO_MM);

function operationMultipliers(operationType) {
  return OPERATION_MULTIPLIERS[operationType] || OPERATION_MULTIPLIERS['General Milling'];
}

function strategyMultipliers(strategyMode) {
  return STRATEGY_MULTIPLIERS[strategyMode] || STRATEGY_MULTIPLIERS.Balanced;
}

function drillingOperationMultipliers(operationType) {
  return DRILLING_OPERATION_MULTIPLIERS[operationType] || DRILLING_OPERATION_MULTIPLIERS['Standard Drill'];
}

function drillingStrategyMultipliers(strategyMode) {
  return DRILLING_STRATEGY_MULTIPLIERS[strategyMode] || DRILLING_STRATEGY_MULTIPLIERS.Balanced;
}

function tappingStrategyMultipliers(strategyMode) {
  return TAPPING_STRATEGY_MULTIPLIERS[strategyMode] || TAPPING_STRATEGY_MULTIPLIERS.Balanced;
}

function tappingBaseRpmByMaterial(materialCategory) {
  if (materialCategory === 'Aluminum') return 220;
  if (materialCategory === 'Steel') return 140;
  if (materialCategory === 'Stainless') return 90;
  if (materialCategory === 'Titanium' || materialCategory === 'Superalloy') return 70;
  return 120;
}

function tappingSmallDiameterRpmCap(diameterIn) {
  const mmDiameter = diameterIn * INCH_TO_MM;
  if (mmDiameter <= 1.6) return 120;
  if (mmDiameter <= 2.5) return 220;
  if (mmDiameter <= 4) return 450;
  if (mmDiameter <= 6) return 700;
  if (mmDiameter <= 10) return 1000;
  return 1400;
}

function nearestStandardDrillSize(diameterIn, unit = 'inch') {
  if (!Number.isFinite(diameterIn)) return null;

  const availableSizes = unit === 'metric' ? STANDARD_METRIC_DRILL_SIZES_IN : STANDARD_DRILL_SIZES_IN;

  let nearest = availableSizes[0];
  let nearestDistance = Math.abs(diameterIn - nearest);

  for (let i = 1; i < availableSizes.length; i += 1) {
    const candidate = availableSizes[i];
    const distance = Math.abs(diameterIn - candidate);

    if (distance < nearestDistance) {
      nearest = candidate;
      nearestDistance = distance;
    }
  }

  return nearest;
}

function toInches(value, unit) {
  if (!Number.isFinite(value)) return null;
  return unit === 'metric' ? value / INCH_TO_MM : value;
}

function toInchesPerMinute(value, unit) {
  if (!Number.isFinite(value)) return null;
  return unit === 'metric' ? value / INCH_TO_MM : value;
}

function toDisplayLength(valueIn, unit) {
  return unit === 'metric' ? valueIn * INCH_TO_MM : valueIn;
}

function toDisplayFeed(valueIpm, unit) {
  return unit === 'metric' ? valueIpm * INCH_TO_MM : valueIpm;
}

function toDisplayChip(valueIn, unit) {
  return unit === 'metric' ? valueIn * INCH_TO_MM : valueIn;
}

function toDisplayMrr(valueIn3Min, unit) {
  return unit === 'metric' ? valueIn3Min * 16.387064 : valueIn3Min;
}

function toDisplayTorque(valueInLb, unit) {
  return unit === 'metric' ? valueInLb * 0.112984829 : valueInLb;
}

function targetChipThickness(chipLoadBase, toolDiameter, toolMaterial) {
  const diamScale = toolDiameter / 0.5;
  const matMult = toolMaterial === 'HSS' ? 0.6 : 1.0;
  return chipLoadBase * diamScale * matMult;
}

function optimalSFM(sfmBase, toolMaterial, coating = 'Uncoated') {
  const toolMult = toolMaterial === 'HSS' ? 0.5 : 1.0;
  const coatingMult = COATING_MULTIPLIERS[coating] ?? 1.0;
  return sfmBase * toolMult * coatingMult;
}

function rpmFromSFM(sfm, diameterIn) {
  return (sfm * 3.82) / diameterIn;
}

function feedRate(rpm, chipThickness, flutes) {
  return rpm * chipThickness * flutes;
}

function radialChipThinningFactor(radialEngagementPct) {
  const ratio = radialEngagementPct / 100;
  if (ratio >= 0.5) return 1.0;
  return 1 / Math.sqrt(ratio);
}

function axialChipThinningFactor(apIn, diamIn) {
  const axialRatio = apIn / diamIn;
  if (!Number.isFinite(axialRatio) || axialRatio <= 0) return 1.0;
  if (axialRatio <= 0.3) return 1.25;
  if (axialRatio < 1.0) return 1.25 - ((axialRatio - 0.3) / 0.7) * 0.25;
  if (axialRatio < 2.0) return 1.0 - ((axialRatio - 1.0) / 1.0) * 0.2;
  return 0.8;
}

function mrr(ae, ap, feedIpm) {
  return ae * ap * feedIpm;
}

function estimatedHP(mrrVal, utsPsi) {
  const kc = utsPsi * 1.5;
  return (mrrVal * kc) / 396000;
}

function toolStiffness(diamIn, stickoutIn, toolMaterial) {
  const E = toolMaterial === 'HSS' ? E_HSS : E_CARBIDE;
  const I = (Math.PI * Math.pow(diamIn, 4)) / 64;
  return (3 * E * I) / Math.pow(stickoutIn, 3);
}

function cuttingForce(utsPsi, chipThickness, apIn, radialPct, diamIn) {
  const kc = 1.5 * utsPsi;
  const ae = (radialPct / 100) * diamIn;
  return kc * chipThickness * apIn * ae / diamIn;
}

function allowableDeflection(stickoutIn) {
  return Math.min(0.0005 * stickoutIn, 0.001);
}

function smallToolPenalty(diamIn) {
  if (diamIn >= 0.25) return 1.0;
  return Math.max(0.4, 0.5 + ((diamIn - 0.0625) / 0.1875) * 0.5);
}

const MIN_CHIP_THICKNESS = 0.00004;

function deflectionGovernor(params) {
  const { utsPsi, chipThickness_material, apIn, radialPct, diamIn, stickoutIn, toolMaterial } = params;

  const k = toolStiffness(diamIn, stickoutIn, toolMaterial);
  const deltaMax = allowableDeflection(stickoutIn);
  const penalty = smallToolPenalty(diamIn);
  let cl = chipThickness_material * penalty;

  let deflectionLimited = false;
  for (let i = 0; i < 50; i++) {
    const Fc = cuttingForce(utsPsi, cl, apIn, radialPct, diamIn);
    const deflection = Fc / k;
    if (deflection <= deltaMax) break;
    cl *= 0.85;
    deflectionLimited = true;
  }

  let reducedForRubbing = false;
  if (cl < MIN_CHIP_THICKNESS) {
    cl = MIN_CHIP_THICKNESS;
    reducedForRubbing = true;
    deflectionLimited = true;
  }

  return { chipLoad: cl, deflectionLimited, reducedForRubbing, estimatedDeflection: cuttingForce(utsPsi, cl, apIn, radialPct, diamIn) / k, allowable: deltaMax };
}

function machinePowerAvailability(rpm, machine) {
  const { spindlePower, torquePeak, torqueTransitionRpm } = machine;
  if (!rpm || rpm <= 0) return spindlePower || null;

  let curveHp = null;
  if (torquePeak && torqueTransitionRpm) {
    const transition = Math.max(200, torqueTransitionRpm);
    const lowRpm = Math.min(rpm, transition);
    const torqueHp = (torquePeak * lowRpm) / HP_CONSTANT;
    const transitionHp = (torquePeak * transition) / HP_CONSTANT;
    curveHp = rpm <= transition ? torqueHp : transitionHp;
  }

  if (curveHp && spindlePower) return Math.min(curveHp, spindlePower);
  return curveHp || spindlePower || null;
}

function machineLimitFlags(rawRpm, rawIpm, rpmLimit, ipmLimit) {
  const rpmCapped = rpmLimit && rawRpm > rpmLimit;
  const ipmCapped = ipmLimit && rawIpm > ipmLimit;
  return { isLimited: !!(rpmCapped || ipmCapped), rpmCapped: !!rpmCapped, ipmCapped: !!ipmCapped };
}

function calculateCore(inputs) {
  const { D, flutes, radialPct, ap, toolMaterial, coating, operationType, strategyMode, stickout, mat, rpmLimit, ipmLimit, machine } = inputs;
  const { tensileUTS, sfmBase, chipLoadBase } = mat;

  const operationMult = operationMultipliers(operationType);
  const strategyMult = strategyMultipliers(strategyMode);

  const clMaterial = targetChipThickness(chipLoadBase, D, toolMaterial) * operationMult.chip * strategyMult.chip;
  const actf = axialChipThinningFactor(ap, D);
  const clAdjusted = clMaterial * actf;

  const { chipLoad: clGoverned, deflectionLimited, reducedForRubbing, estimatedDeflection, allowable } = deflectionGovernor({
    utsPsi: tensileUTS,
    chipThickness_material: clAdjusted,
    apIn: ap,
    radialPct,
    diamIn: D,
    stickoutIn: stickout,
    toolMaterial,
  });

  const chipThickness = Math.min(clAdjusted, clGoverned);

  const sfm = optimalSFM(sfmBase, toolMaterial, coating) * operationMult.sfm * strategyMult.sfm;
  const rpmMultiplier = reducedForRubbing ? 0.7 : 1.0;
  const rawRpm = rpmFromSFM(sfm, D) * rpmMultiplier;

  const rctf = radialChipThinningFactor(radialPct);
  const rawFeed = feedRate(rawRpm, chipThickness, flutes);
  const rawAdjustedFeed = rawFeed * rctf;

  const limits = machineLimitFlags(rawRpm, rawAdjustedFeed, rpmLimit, ipmLimit);
  const rpm = limits.rpmCapped ? rpmLimit : rawRpm;
  const uncappedFeed = limits.rpmCapped ? feedRate(rpm, chipThickness, flutes) * rctf : rawAdjustedFeed;
  let feedIpm = (ipmLimit && ipmLimit < uncappedFeed) ? ipmLimit : uncappedFeed;

  const ae = (radialPct / 100) * D;
  let mrrVal = mrr(ae, ap, feedIpm);
  let hp = estimatedHP(mrrVal, tensileUTS);

  const availableHp = machinePowerAvailability(rpm, machine);
  const powerCurveLimited = !!(availableHp && hp > availableHp);
  if (powerCurveLimited && hp > 0) {
    const reduction = availableHp / hp;
    feedIpm *= reduction;
    mrrVal = mrr(ae, ap, feedIpm);
    hp = estimatedHP(mrrVal, tensileUTS);
  }

  return {
    sfm,
    rpm,
    feedIpm,
    chipThickness,
    mrr: mrrVal,
    hp,
    ae,
    rctf,
    actf,
    radialPct,
    operationType,
    strategyMode,
    limits,
    deflectionLimited,
    reducedForRubbing,
    estimatedDeflection,
    allowableDeflection: allowable,
    powerCurveLimited,
    availableHp,
    spindlePower: machine.spindlePower,
    stiffnessLimitNote: deflectionLimited
      ? reducedForRubbing
        ? 'Feed & speed reduced — chip thickness at rubbing floor'
        : 'Feed limited by tool stiffness'
      : null,
  };
}

function optimizeRoughing(baseInputs) {
  const radialCandidates = [10, 15, 20, 25, 30, 40, 50];
  const axialRatios = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
  const scenarios = [];

  radialCandidates.forEach((radialPct) => {
    axialRatios.forEach((ratio) => {
      const ap = baseInputs.D * ratio;
      const scenario = calculateCore({ ...baseInputs, radialPct, ap });
      const safe = !scenario.reducedForRubbing && !scenario.powerCurveLimited && scenario.estimatedDeflection <= scenario.allowableDeflection;
      scenarios.push({ ...scenario, safe, ap });
    });
  });

  return scenarios
    .filter(s => s.safe)
    .sort((a, b) => b.mrr - a.mrr)
    .slice(0, 3)
    .map(s => ({
      radialPct: s.radialPct,
      axialDepth: Math.round(safeNumber(s.ap) * 1000) / 1000,
      feedIpm: Math.round(s.feedIpm * 10) / 10,
      rpm: Math.round(s.rpm),
      mrr: Math.round(s.mrr * 1000) / 1000,
      hp: Math.round(s.hp * 100) / 100,
    }));
}

function safeNumber(value) {
  return Number.isFinite(value) ? value : 0;
}

function runCalculations(values, unit = 'inch', machineSetup = null) {
  const D = toInches(parseFloat(values.toolDiameter), unit);
  const flutes = parseInt(values.fluteCount, 10);
  const radialPct = parseFloat(values.radialEngagement);
  const ap = toInches(parseFloat(values.axialDepth), unit);
  const toolMaterial = values.toolMaterial || 'Carbide';
  const coating = values.coating || 'Uncoated';
  const operationType = values.operationType || 'General Milling';
  const strategyMode = values.strategyMode || 'Balanced';

  const stickoutInput = toInches(parseFloat(values.stickout), unit);
  const stickout = stickoutInput || (D * 3);

  const mat = values.customMaterial || MATERIAL_LIBRARY.find(m => m.name === values.materialName);

  if (!D || !flutes || !radialPct || !ap || !mat) return null;

  const rpmLimit = parseFloat(machineSetup?.maxRpm ?? values.maxRpm) || null;
  const ipmLimit = toInchesPerMinute(parseFloat(machineSetup?.maxFeedrate ?? values.maxFeedrate), unit) || null;
  const machine = {
    spindlePower: parseFloat(machineSetup?.spindlePower ?? values.spindlePower) || null,
    torquePeak: parseFloat(machineSetup?.peakTorque ?? values.peakTorque) || null,
    torqueTransitionRpm: parseFloat(machineSetup?.torqueTransitionRpm ?? values.torqueTransitionRpm) || null,
  };

  const core = calculateCore({ D, flutes, radialPct, ap, toolMaterial, coating, operationType, strategyMode, stickout, mat, rpmLimit, ipmLimit, machine });
  const roughingSuggestions = optimizeRoughing({ D, flutes, toolMaterial, coating, operationType: 'Adaptive Roughing', strategyMode, stickout, mat, rpmLimit, ipmLimit, machine });

  const activeLimits = [];
  if (core.stiffnessLimitNote) activeLimits.push('Deflection');
  if (core.powerCurveLimited) activeLimits.push('Power Curve');
  if (core.limits?.rpmCapped || core.limits?.ipmCapped) activeLimits.push('Machine');

  return {
    sfm: Math.round(core.sfm),
    rpm: Math.round(core.rpm),
    feedIpm: Math.round(toDisplayFeed(core.feedIpm, unit) * 10) / 10,
    chipThickness: Math.round(toDisplayChip(core.chipThickness, unit) * 100000) / 100000,
    mrr: Math.round(toDisplayMrr(core.mrr, unit) * 1000) / 1000,
    hp: Math.round(core.hp * 100) / 100,
    ae: toDisplayLength(core.ae, unit),
    ap: toDisplayLength(ap, unit),
    limits: core.limits,
    spindlePower: machine.spindlePower,
    rctf: Math.round(core.rctf * 100) / 100,
    actf: Math.round(core.actf * 100) / 100,
    radialPct,
    operationType,
    strategyMode,
    machineName: machineSetup?.name || null,
    deflectionLimited: core.deflectionLimited,
    reducedForRubbing: core.reducedForRubbing,
    estimatedDeflection: Math.round(toDisplayLength(core.estimatedDeflection, unit) * 100000) / 100000,
    allowableDeflection: Math.round(toDisplayLength(core.allowableDeflection, unit) * 100000) / 100000,
    stiffnessLimitNote: core.stiffnessLimitNote,
    powerCurveLimited: core.powerCurveLimited,
    availableHp: core.availableHp ? Math.round(core.availableHp * 100) / 100 : null,
    governingLimit: activeLimits.length ? activeLimits.join(' + ') : 'Material Model',
    roughingSuggestions: roughingSuggestions.map(s => ({
      ...s,
      feed: Math.round(toDisplayFeed(s.feedIpm, unit) * 10) / 10,
      ae: Math.round(toDisplayLength((s.radialPct / 100) * D, unit) * 1000) / 1000,
      ap: Math.round(toDisplayLength(s.axialDepth, unit) * 1000) / 1000,
      mrr: Math.round(toDisplayMrr(s.mrr, unit) * 1000) / 1000,
    })),
  };
}

function runDrillingCalculations(values, unit = 'inch', machineSetup = null) {
  const D = toInches(parseFloat(values.toolDiameter), unit);
  const holeDepth = toInches(parseFloat(values.holeDepth), unit);
  const toolMaterial = values.toolMaterial || 'Carbide';
  const coating = values.coating || 'Uncoated';
  const operationType = values.drillOperationType || 'Standard Drill';
  const strategyMode = values.drillStrategyMode || 'Balanced';
  const pointAngle = parseFloat(values.pointAngle) || 118;

  const mat = values.customMaterial || MATERIAL_LIBRARY.find(m => m.name === values.materialName);
  if (!D || !holeDepth || !mat) return null;

  const rpmLimit = parseFloat(machineSetup?.maxRpm ?? values.maxRpm) || null;
  const ipmLimit = toInchesPerMinute(parseFloat(machineSetup?.maxFeedrate ?? values.maxFeedrate), unit) || null;
  const machine = {
    spindlePower: parseFloat(machineSetup?.spindlePower ?? values.spindlePower) || null,
    torquePeak: parseFloat(machineSetup?.peakTorque ?? values.peakTorque) || null,
    torqueTransitionRpm: parseFloat(machineSetup?.torqueTransitionRpm ?? values.torqueTransitionRpm) || null,
  };

  const operationMult = drillingOperationMultipliers(operationType);
  const strategyMult = drillingStrategyMultipliers(strategyMode);
  const sfm = optimalSFM(mat.sfmBase, toolMaterial, coating) * operationMult.sfm * strategyMult.sfm;
  const rawRpm = rpmFromSFM(sfm, D);
  const rpm = rpmLimit ? Math.min(rawRpm, rpmLimit) : rawRpm;

  const chipPerLip = targetChipThickness(mat.chipLoadBase, D, toolMaterial);
  const feedPerRev = chipPerLip * 2 * operationMult.fpr * strategyMult.fpr;

  const uncappedFeedIpm = rpm * feedPerRev;
  let feedIpm = ipmLimit ? Math.min(uncappedFeedIpm, ipmLimit) : uncappedFeedIpm;

  const crossSectionArea = Math.PI * Math.pow(D / 2, 2);
  let mrrVal = crossSectionArea * feedIpm;
  let hp = estimatedHP(mrrVal, mat.tensileUTS);

  const availableHp = machinePowerAvailability(rpm, machine);
  const powerCurveLimited = !!(availableHp && hp > availableHp);
  if (powerCurveLimited && hp > 0) {
    const reduction = availableHp / hp;
    feedIpm *= reduction;
    mrrVal = crossSectionArea * feedIpm;
    hp = estimatedHP(mrrVal, mat.tensileUTS);
  }

  const depthRatio = holeDepth / D;
  const deepHoleFactor = depthRatio > 3 ? 0.75 : 1.0;
  const peckDistance = Math.min(holeDepth, D * 1.5 * operationMult.peck * strategyMult.peck * deepHoleFactor);

  return {
    sfm: Math.round(sfm),
    rpm: Math.round(rpm),
    feedPerRev: Math.round(toDisplayChip(feedPerRev, unit) * 10000) / 10000,
    feedIpm: Math.round(toDisplayFeed(feedIpm, unit) * 10) / 10,
    peckDistance: Math.round(toDisplayLength(peckDistance, unit) * 1000) / 1000,
    holeDepth: Math.round(toDisplayLength(holeDepth, unit) * 1000) / 1000,
    mrr: Math.round(toDisplayMrr(mrrVal, unit) * 1000) / 1000,
    hp: Math.round(hp * 100) / 100,
    operationType,
    strategyMode,
    pointAngle,
    machineName: machineSetup?.name || null,
    availableHp: availableHp ? Math.round(availableHp * 100) / 100 : null,
    powerCurveLimited,
    limits: {
      isLimited: Boolean((rpmLimit && rawRpm > rpmLimit) || (ipmLimit && uncappedFeedIpm > ipmLimit)),
      rpmCapped: Boolean(rpmLimit && rawRpm > rpmLimit),
      ipmCapped: Boolean(ipmLimit && uncappedFeedIpm > ipmLimit),
    },
  };
}

function runTappingCalculations(values, unit = 'inch', machineSetup = null) {
  const majorDiameter = toInches(parseFloat(values.tapMajorDiameter), unit);
  const pitch = toInches(parseFloat(values.threadPitch), unit);
  const threadEngagement = parseFloat(values.threadEngagement);
  const holeDepth = toInches(parseFloat(values.holeDepth), unit);
  const tapMaterial = values.tapMaterial || 'HSS';
  const strategyMode = values.tappingStrategy || 'Balanced';

  const mat = values.customMaterial || MATERIAL_LIBRARY.find(m => m.name === values.materialName);
  if (!majorDiameter || !pitch || !threadEngagement || !holeDepth || !mat) return null;

  const rpmLimit = parseFloat(machineSetup?.maxRpm ?? values.maxRpm) || null;
  const ipmLimit = toInchesPerMinute(parseFloat(machineSetup?.maxFeedrate ?? values.maxFeedrate), unit) || null;
  const machine = {
    spindlePower: parseFloat(machineSetup?.spindlePower ?? values.spindlePower) || null,
    torquePeak: parseFloat(machineSetup?.peakTorque ?? values.peakTorque) || null,
    torqueTransitionRpm: parseFloat(machineSetup?.torqueTransitionRpm ?? values.torqueTransitionRpm) || null,
  };

  const strategyMult = tappingStrategyMultipliers(strategyMode);
  const baseRpm = tappingBaseRpmByMaterial(mat.category);
  const depthRatio = holeDepth / majorDiameter;
  const depthRpmFactor = depthRatio > 2 ? 0.55 : depthRatio > 1 ? 0.75 : 1.0;
  const smallDiameterCap = tappingSmallDiameterRpmCap(majorDiameter);
  const engagementRpmFactor = threadEngagement > 75 ? 0.8 : threadEngagement < 55 ? 1.1 : 1.0;

  const rawRpm = baseRpm * strategyMult.rpm * depthRpmFactor * engagementRpmFactor;
  const rpmBeforeMachine = Math.min(rawRpm, smallDiameterCap);
  const rpm = rpmLimit ? Math.min(rpmBeforeMachine, rpmLimit) : rpmBeforeMachine;
  const sfm = (rpm * majorDiameter) / 3.82;

  const feedPerRev = pitch;
  const uncappedFeedIpm = rpm * feedPerRev;
  const feedIpm = ipmLimit ? Math.min(uncappedFeedIpm, ipmLimit) : uncappedFeedIpm;

  const engagementRatio = Math.max(0.4, Math.min(0.95, threadEngagement / 100));
  const engagementFactor = engagementRatio / 0.75;
  const tapDrillDiameter = Math.max(majorDiameter - (pitch * engagementFactor), 0.01);
  const clampedTapDrillDiameter = nearestStandardDrillSize(tapDrillDiameter, unit) || tapDrillDiameter;

  const depthMultiplier = depthRatio > 2 ? 1.5 : depthRatio > 1 ? 1.25 : 1.0;

  const materialShearPsi = mat.tensileUTS * 0.6;
  const geometryK = 0.24;
  const torqueInLb = geometryK * materialShearPsi * Math.pow(majorDiameter, 3) * engagementRatio * depthMultiplier * strategyMult.torque;

  const coreDiameter = majorDiameter * 0.8;
  const coreRadius = coreDiameter / 2;
  const shearStressPsi = (2 * torqueInLb) / (Math.PI * Math.pow(coreRadius, 3));
  const shearStressKsi = shearStressPsi / 1000;

  const allowableShearKsi = TAP_ALLOWABLE_SHEAR_KSI[tapMaterial] || TAP_ALLOWABLE_SHEAR_KSI.HSS;
  const safetyFactor = allowableShearKsi / shearStressKsi;

  const crossSectionArea = Math.PI * Math.pow(majorDiameter / 2, 2);
  const mrrVal = crossSectionArea * feedIpm;
  const hp = estimatedHP(mrrVal, mat.tensileUTS);

  const warnings = [];
  if (threadEngagement > 75) warnings.push('Thread engagement is high and increases torque sharply; target 60–70%.');
  if (depthRatio > 2) warnings.push('Hole depth exceeds 2×D and adds significant tapping torque.');
  if (rpmBeforeMachine < rawRpm) warnings.push('RPM reduced for small tap diameter to avoid torsional spikes.');
  if (safetyFactor < 1.0) warnings.push('Tap shear stress exceeds allowable limit; breakage risk is high.');
  else if (safetyFactor < 1.5) warnings.push('Safety factor is low; reduce speed or thread engagement.');

  return {
    sfm: Math.round(sfm),
    rpm: Math.round(rpm),
    feedPerRev: Math.round(toDisplayChip(feedPerRev, unit) * 10000) / 10000,
    feedIpm: Math.round(toDisplayFeed(feedIpm, unit) * 10) / 10,
    tapDrillDiameter: Math.round(toDisplayLength(clampedTapDrillDiameter, unit) * 1000) / 1000,
    torque: Math.round(toDisplayTorque(torqueInLb, unit) * 100) / 100,
    shearStress: Math.round(shearStressKsi * 100) / 100,
    safetyFactor: Math.round(safetyFactor * 100) / 100,
    allowableShear: allowableShearKsi,
    threadEngagement,
    holeDepth: Math.round(toDisplayLength(holeDepth, unit) * 1000) / 1000,
    tapMaterial,
    strategyMode,
    operationType: 'Tapping',
    machineName: machineSetup?.name || null,
    hp: Math.round(hp * 100) / 100,
    mrr: Math.round(toDisplayMrr(mrrVal, unit) * 1000) / 1000,
    limits: {
      isLimited: Boolean((rpmLimit && rawRpm > rpmLimit) || (ipmLimit && uncappedFeedIpm > ipmLimit)),
      rpmCapped: Boolean(rpmLimit && rawRpm > rpmLimit),
      ipmCapped: Boolean(ipmLimit && uncappedFeedIpm > ipmLimit),
    },
    warnings,
  };
}

global.GPM = global.GPM || {};
Object.assign(global.GPM, {
  MATERIAL_LIBRARY: MATERIAL_LIBRARY,
  COATING_MULTIPLIERS: COATING_MULTIPLIERS,
  STRATEGY_MULTIPLIERS: STRATEGY_MULTIPLIERS,
  runCalculations: runCalculations,
  runDrillingCalculations: runDrillingCalculations,
  runTappingCalculations: runTappingCalculations
});
})(window);
